import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Notification Core (spec 29)
// Un système commun permettant à tous les modules d'émettre des
// notifications sur plusieurs canaux (in-app, email, SMS, WhatsApp,
// push). Zifek Core ne force pas l'envoi effectif de chaque canal :
// il fournit le registre, les statuts et les modèles réutilisables.
// ─────────────────────────────────────────────────────────────

export type NotificationChannel = 'in_app' | 'email' | 'sms' | 'whatsapp' | 'push';
export type NotificationStatus = 'pending' | 'sent' | 'read' | 'failed';

export interface CoreNotification {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  channel: NotificationChannel;
  type: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  recipient_user_id: number | null;
  recipient: string | null;
  status: NotificationStatus;
  read_at: string | null;
  sent_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export interface CoreNotificationTemplate {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  type: string;
  channel: NotificationChannel;
  label: string | null;
  title_template: string | null;
  body_template: string | null;
  enabled: boolean | null;
  created_at: string | null;
}

/** Canaux disponibles et leur état d'intégration. */
export const NOTIFICATION_CHANNELS: { key: NotificationChannel; label: string; icon: string; available: boolean }[] = [
  { key: 'in_app', label: 'In-app', icon: 'ri-notification-3-line', available: true },
  { key: 'email', label: 'Email', icon: 'ri-mail-line', available: true },
  { key: 'sms', label: 'SMS', icon: 'ri-message-2-line', available: false },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line', available: false },
  { key: 'push', label: 'Push', icon: 'ri-smartphone-line', available: true },
];

export function channelMeta(key: string | null | undefined) {
  return NOTIFICATION_CHANNELS.find((c) => c.key === key) || NOTIFICATION_CHANNELS[0];
}

/** Types d'événements courants émis par les modules. */
export const NOTIFICATION_TYPES: { key: string; label: string; icon: string; defaultTitle: string; defaultBody: string }[] = [
  { key: 'booking_confirmed', label: 'Réservation confirmée', icon: 'ri-calendar-check-line', defaultTitle: 'Votre réservation est confirmée', defaultBody: 'Votre réservation a bien été confirmée.' },
  { key: 'appointment_reminder', label: 'Rappel de rendez-vous', icon: 'ri-alarm-line', defaultTitle: 'Rappel de rendez-vous', defaultBody: 'Rappel : vous avez un rendez-vous prévu.' },
  { key: 'invoice_overdue', label: 'Facture en retard', icon: 'ri-file-warning-line', defaultTitle: 'Facture en retard', defaultBody: 'Une facture arrive à échéance.' },
  { key: 'order_shipped', label: 'Commande expédiée', icon: 'ri-truck-line', defaultTitle: 'Votre commande est en route', defaultBody: 'Votre commande vient d’être expédiée.' },
  { key: 'payment_received', label: 'Paiement reçu', icon: 'ri-money-dollar-circle-line', defaultTitle: 'Paiement reçu', defaultBody: 'Un paiement a été enregistré.' },
  { key: 'ai_customer_search', label: 'Recherche client IA', icon: 'ri-robot-2-line', defaultTitle: 'Un client cherche votre offre', defaultBody: 'Un client a recherché votre service ou produit via l’assistant IA.' },
  { key: 'system', label: 'Système', icon: 'ri-information-line', defaultTitle: 'Notification', defaultBody: '' },
];

export function notificationTypeMeta(key: string | null | undefined) {
  return NOTIFICATION_TYPES.find((t) => t.key === key) || NOTIFICATION_TYPES[NOTIFICATION_TYPES.length - 1];
}

export const NOTIFICATION_STATUS_META: Record<NotificationStatus, { label: string; className: string }> = {
  pending: { label: 'En attente', className: 'bg-background-200/70 text-foreground-600' },
  sent: { label: 'Envoyée', className: 'bg-secondary-100 text-secondary-700' },
  read: { label: 'Lue', className: 'bg-primary-100 text-primary-700' },
  failed: { label: 'Échec', className: 'bg-red-100 text-red-600' },
};

export function notificationStatusMeta(key: string | null | undefined) {
  return NOTIFICATION_STATUS_META[(key as NotificationStatus)] || NOTIFICATION_STATUS_META.pending;
}

// ── Lectures ──

export async function listCoreNotifications(organizationId: number): Promise<CoreNotification[]> {
  const { data, error } = await supabase
    .from('core_notifications')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreNotification[];
}

export async function countUnreadNotifications(organizationId: number): Promise<number> {
  const { count, error } = await supabase
    .from('core_notifications')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('status', 'pending');
  if (error) return 0;
  return count ?? 0;
}

// ── Mutations ──

export interface NotificationInput {
  tenantId: number | null;
  organizationId: number;
  channel?: NotificationChannel;
  type?: string;
  title: string;
  body?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  recipientUserId?: number | null;
  recipient?: string | null;
  status?: NotificationStatus;
  metadata?: Record<string, unknown> | null;
}

/** Émet une notification (registre central). Sert à tous les modules. */
export async function emitCoreNotification(input: NotificationInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_notifications')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      channel: input.channel || 'in_app',
      type: input.type || 'system',
      title: input.title,
      body: input.body || null,
      entity_type: input.entityType || null,
      entity_id: input.entityId || null,
      recipient_user_id: input.recipientUserId ?? null,
      recipient: input.recipient || null,
      status: input.status || (input.channel && input.channel !== 'in_app' ? 'sent' : 'pending'),
      sent_at: input.status === 'sent' ? new Date().toISOString() : null,
      metadata: input.metadata || {},
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function markNotificationRead(id: number): Promise<void> {
  const { error } = await supabase
    .from('core_notifications')
    .update({ status: 'read', read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function markAllNotificationsRead(organizationId: number): Promise<void> {
  const { error } = await supabase
    .from('core_notifications')
    .update({ status: 'read', read_at: new Date().toISOString() })
    .eq('organization_id', organizationId)
    .neq('status', 'read');
  if (error) throw error;
}

export async function deleteCoreNotification(id: number): Promise<void> {
  const { error } = await supabase.from('core_notifications').delete().eq('id', id);
  if (error) throw error;
}

// ── Modèles réutilisables ──

export async function listCoreNotificationTemplates(organizationId: number): Promise<CoreNotificationTemplate[]> {
  const { data, error } = await supabase
    .from('core_notification_templates')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreNotificationTemplate[];
}

export async function createCoreNotificationTemplate(input: {
  tenantId: number | null;
  organizationId: number;
  type: string;
  channel: NotificationChannel;
  label?: string | null;
  titleTemplate?: string | null;
  bodyTemplate?: string | null;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_notification_templates')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      type: input.type,
      channel: input.channel,
      label: input.label || null,
      title_template: input.titleTemplate || null,
      body_template: input.bodyTemplate || null,
      enabled: true,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreNotificationTemplate(
  id: number,
  patch: { titleTemplate?: string | null; bodyTemplate?: string | null; enabled?: boolean; channel?: NotificationChannel; type?: string },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.titleTemplate !== undefined) payload.title_template = patch.titleTemplate || null;
  if (patch.bodyTemplate !== undefined) payload.body_template = patch.bodyTemplate || null;
  if (patch.enabled !== undefined) payload.enabled = patch.enabled;
  if (patch.channel !== undefined) payload.channel = patch.channel;
  if (patch.type !== undefined) payload.type = patch.type;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_notification_templates').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreNotificationTemplate(id: number): Promise<void> {
  const { error } = await supabase.from('core_notification_templates').delete().eq('id', id);
  if (error) throw error;
}